import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

declare module '@mui/material/styles' {
  interface Palette {
    red: Palette['primary'];
    yellow: Palette['primary'];
    green: Palette['primary'];
    black: Palette['primary'];
    violet: Palette['primary'];
  }
  interface PaletteOptions {
    red: PaletteOptions['primary'];
    yellow: PaletteOptions['primary'];
    green: PaletteOptions['primary'];
    black: PaletteOptions['primary'];
    violet: PaletteOptions['primary'];
  }
}

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('themeMode');
      return (saved as ThemeMode) || 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('themeMode', mode);
      document.documentElement.setAttribute('data-theme', mode);
    }
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#5073F8',
            light: '#7A96FB',
            dark: '#3D5BD4',
            contrastText: '#FFFFFF'
          },
          secondary: {
            main: '#424242',
            light: '#6D6D6D',
            dark: '#1B1B1B',
            contrastText: '#FFFFFF'
          },
          background: {
            default: mode === 'light' ? '#F5F5F5' : '#121212',
            paper: mode === 'light' ? '#FFFFFF' : '#1E1E1E'
          },
          text: {
            primary: mode === 'light' ? '#1E1E1E' : '#FFFFFF',
            secondary: mode === 'light' ? '#616161' : '#B0B0B0'
          },
          red: {
            main: '#EF5350'
          },
          yellow: {
            main: '#FFEB3B'
          },
          violet: {
            main: '#AB47BC'
          },
          black: {
            main: '#000000'
          },
          green: {
            main: '#66BB6A'
          },
          divider: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
          action: {
            active: '#5073F8',
            hover: mode === 'light' ? 'rgba(140, 0, 255, 0.15)' : 'rgba(140, 0, 255, 0.08)',
            selected: mode === 'light' ? 'rgba(140, 0, 255, 0.25)' : 'rgba(140, 0, 255, 0.16)',
            disabled: mode === 'light' ? 'rgba(0, 0, 0, 0.26)' : 'rgba(255, 255, 255, 0.3)',
            disabledBackground: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'
          }
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontWeight: 700,
            fontSize: '2.5rem',
            letterSpacing: '-0.01562em'
          },
          h2: {
            fontWeight: 600,
            fontSize: '2rem',
            letterSpacing: '-0.00833em'
          },
          h3: {
            fontWeight: 600,
            fontSize: '1.75rem',
            letterSpacing: '0em'
          },
          h4: {
            fontWeight: 600,
            fontSize: '1.5rem',
            letterSpacing: '0.00735em'
          },
          h5: {
            fontWeight: 600,
            fontSize: '1.25rem',
            letterSpacing: '0em'
          },
          h6: {
            fontWeight: 600,
            fontSize: '1rem',
            letterSpacing: '0.0075em'
          },
          button: {
            fontWeight: 600,
            textTransform: 'none'
          }
        },
        shape: {
          borderRadius: 12
        },
        components: {
          MuiTextField: {
            defaultProps: {
              variant: 'outlined'
            },
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(140, 0, 255, 0.5)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5073F8'
                  }
                }
              }
            }
          },
          MuiButton: {
            styleOverrides: {
              root: ({ theme }) => ({
                borderRadius: 8,
                padding: '10px 24px',
                fontSize: '0.95rem',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(140, 0, 255, 0.3)'
                },
                // Дефолтні 24px по боках з'їдають рядок уже двома кнопками.
                [theme.breakpoints.down('sm')]: {
                  padding: '8px 16px',
                  fontSize: '0.875rem'
                }
              }),
              contained: {
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(140, 0, 255, 0.4)'
                }
              },
              containedPrimary: {
                color: '#FFFFFF !important',
                '&:hover': {
                  color: '#FFFFFF !important'
                },
                '&:active': {
                  color: '#FFFFFF !important'
                },
                '&:focus': {
                  color: '#FFFFFF !important'
                },
                '& .MuiButton-label': {
                  color: '#FFFFFF !important'
                }
              },
              outlined: {
                borderColor: '#878787',
                color: mode === 'light' ? '#1E1E1E' : '#FFFFFF',
                '&:hover': {
                  borderColor: '#878787',
                  backgroundColor: 'rgba(135, 135, 135, 0.08)'
                }
              },
              textPrimary: {
                color: mode === 'light' ? '#3D5BD4 !important' : '#5073F8 !important',
                '&:hover': {
                  backgroundColor: mode === 'light' ? 'rgba(140, 0, 255, 0.08)' : 'rgba(140, 0, 255, 0.08)',
                  color: mode === 'light' ? '#3D5BD4 !important' : '#5073F8 !important'
                },
                '&:active': {
                  color: mode === 'light' ? '#3D5BD4 !important' : '#5073F8 !important'
                },
                '& .MuiButton-startIcon': {
                  color: mode === 'light' ? '#3D5BD4 !important' : '#5073F8 !important'
                }
              }
            }
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                backgroundColor: mode === 'light' ? '#FFFFFF' : '#1E1E1E',
                boxShadow: mode === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 20px rgba(0, 0, 0, 0.5)'
              },
              elevation1: {
                boxShadow: mode === 'light' ? '0 1px 4px rgba(0, 0, 0, 0.08)' : '0 2px 8px rgba(0, 0, 0, 0.4)'
              },
              elevation2: {
                boxShadow: mode === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 12px rgba(0, 0, 0, 0.5)'
              },
              elevation3: {
                boxShadow: mode === 'light' ? '0 4px 12px rgba(0, 0, 0, 0.12)' : '0 8px 24px rgba(0, 0, 0, 0.6)'
              }
            }
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 16,
                backgroundColor: mode === 'light' ? '#FFFFFF' : '#1E1E1E',
                boxShadow: mode === 'light' ? '0 2px 12px rgba(0, 0, 0, 0.08)' : '0 4px 20px rgba(0, 0, 0, 0.5)'
              }
            }
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === 'light' ? '#FFFFFF' : '#1A1A1A',
                borderRight: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(255, 255, 255, 0.12)'
              }
            }
          },
          MuiListItem: {
            styleOverrides: {
              root: {
                '&.Mui-selected': {
                  backgroundColor: 'rgba(140, 0, 255, 0.16)',
                  '&:hover': {
                    backgroundColor: 'rgba(140, 0, 255, 0.24)'
                  }
                }
              }
            }
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                color: '#5073F8',
                '&:hover': {
                  backgroundColor: 'rgba(140, 0, 255, 0.08)'
                }
              }
            }
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                '&.MuiTableRow-hover:hover': {
                  backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
                  cursor: 'pointer'
                }
              }
            }
          },

          // --- Мобільна адаптація ---
          // Тут лише те, що має діяти глобально: інакше довелось би правити
          // 63 діалоги і 56 таблиць поштучно. Перевизначення теми
          // застосовуються після внутрішніх стилів компонента, тому
          // перебивають і maxWidth від пропса maxWidth у Dialog.

          // Діалог на телефоні — на весь екран. Модалка на 600px посеред
          // екрана шириною 390px не має сенсу: половина форми за краєм.
          MuiDialog: {
            styleOverrides: {
              paper: ({ theme }) => ({
                [theme.breakpoints.down('sm')]: {
                  margin: 0,
                  width: '100%',
                  maxWidth: '100%',
                  height: '100%',
                  maxHeight: '100%',
                  borderRadius: 0
                }
              })
            }
          },
          MuiDialogContent: {
            styleOverrides: {
              root: ({ theme }) => ({
                [theme.breakpoints.down('sm')]: {
                  padding: theme.spacing(2)
                }
              })
            }
          },
          MuiDialogActions: {
            styleOverrides: {
              root: ({ theme }) => ({
                [theme.breakpoints.down('sm')]: {
                  padding: theme.spacing(1.5, 2),
                  // Кнопки в ряд не влазять — ставимо їх стовпцем на всю ширину.
                  flexDirection: 'column-reverse',
                  alignItems: 'stretch',
                  gap: theme.spacing(1),
                  '& > :not(style) ~ :not(style)': {
                    marginLeft: 0
                  }
                }
              })
            }
          },

          // Таблиця нікуди не дінеться з 10 колонками, тому даємо їй
          // горизонтальний скрол замість того, щоб рвати сторінку.
          MuiTableContainer: {
            styleOverrides: {
              root: {
                maxWidth: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch'
              }
            }
          },
          MuiTableCell: {
            styleOverrides: {
              root: ({ theme }) => ({
                [theme.breakpoints.down('sm')]: {
                  padding: theme.spacing(1),
                  fontSize: '0.8125rem',
                  whiteSpace: 'nowrap'
                }
              })
            }
          }
        }
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
